import { TIER_LABELS } from './channelConfig';
import { OFFER_BENEFITS } from './offersCopy';

function QuotaMeter({
  label,
  used,
  standardMax,
  bonusMax,
  totalMax,
  remaining,
  bonusLines = [],
  hint,
}) {
  const bonus = bonusMax || 0;
  const standard = standardMax || 0;
  const total = totalMax || standard + bonus;

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">
        {remaining}{' '}
        <span className="text-base font-normal text-gray-500">
          restante{remaining > 1 ? 's' : ''}
        </span>
      </p>
      {bonus > 0 ? (
        <p className="mt-1 text-xs text-gray-600">
          {standard} inclus + {bonus} offert{bonus > 1 ? 's' : ''} = {total} disponible{total > 1 ? 's' : ''}
        </p>
      ) : (
        <p className="mt-1 text-xs text-gray-600">{standard} inclus ce mois-ci</p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        {used} utilisée{used > 1 ? 's' : ''}
        {total > 0 ? ` · ${remaining} restante${remaining > 1 ? 's' : ''}` : ''}
      </p>
      {bonusLines.map((line) => (
        <p key={line.id || line.label} className="mt-2 text-xs font-medium text-sky-800">
          {line.label}
        </p>
      ))}
      {hint ? <p className="mt-2 text-xs text-gray-400">{hint}</p> : null}
      {total > 0 && (
        <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full"
            style={{ width: `${Math.min(100, (used / total) * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default function CommunicationQuotaBar({
  entitlements,
  marketingEnabled,
  platformMode = false,
  onDiscoverOffers,
}) {
  if (platformMode) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="card">
            <p className="text-sm text-gray-500">Périmètre</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">Plateforme</p>
            <p className="mt-1 text-xs text-gray-500">Communication Clickorico (hors boutiques)</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Canal</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">In-App</p>
            <p className="mt-1 text-xs text-gray-500">Tous les clients actifs Clickorico</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-500">Offres commerçants</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">—</p>
            <p className="mt-1 text-xs text-gray-500">Gérées sur chaque fiche commerce</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Pour une campagne boutique (email + In-App), ouvrez la fiche du commerçant → Communication.
        </p>
      </div>
    );
  }

  if (!entitlements) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card h-24 animate-pulse bg-gray-50" />
        ))}
      </div>
    );
  }

  const rawTier = String(entitlements.tier || 'INCLUS').toUpperCase();
  const displayTier = rawTier === 'PREMIUM' ? 'PLUS' : rawTier === 'PLUS' ? 'PLUS' : 'INCLUS';
  const offer = OFFER_BENEFITS[displayTier] || OFFER_BENEFITS.INCLUS;
  const caps = entitlements.caps || {};
  const usage = entitlements.usage || {};
  const remaining = entitlements.remaining || {};
  const standard = entitlements.standard || {};
  const bonus = entitlements.bonus || {};
  const capacity = entitlements.capacity || {};
  const bonusSummary = Array.isArray(entitlements.bonusSummary) ? entitlements.bonusSummary : [];

  const emailUsed = usage.emailCampaignCount || 0;
  const emailStandard = standard.email ?? caps.emailCampaignsPerMonth ?? 0;
  const emailBonus = bonus.email ?? 0;
  const emailTotal = capacity.email ?? emailStandard + emailBonus;
  const emailRemaining = remaining.email ?? Math.max(0, emailTotal - emailUsed);

  const inAppUsed = usage.inAppCampaignCount || 0;
  const inAppStandard = standard.inApp ?? caps.inAppCampaignsPerMonth ?? 0;
  const inAppBonus = bonus.inApp ?? 0;
  const inAppTotal = capacity.inApp ?? inAppStandard + inAppBonus;
  const inAppRemaining = remaining.inApp ?? Math.max(0, inAppTotal - inAppUsed);

  const emailBonusLines = bonusSummary.filter((item) => item.channel === 'EMAIL');
  const inAppBonusLines = bonusSummary.filter((item) => item.channel === 'IN_APP');

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card">
          <p className="text-sm text-gray-500">Votre offre</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {offer.label || TIER_LABELS[displayTier]}
          </p>
          <p className="mt-1 text-sm text-gray-600">{offer.tagline}</p>
          <ul className="mt-3 space-y-1 text-xs text-gray-600">
            {offer.bullets.slice(0, 3).map((b) => (
              <li key={b}>• {b}</li>
            ))}
          </ul>
        </div>
        <div className="card">
          <QuotaMeter
            label="Campagnes email"
            used={emailUsed}
            standardMax={emailStandard}
            bonusMax={emailBonus}
            totalMax={emailTotal}
            remaining={emailRemaining}
            bonusLines={emailBonusLines}
            hint={
              caps.maxEmailRecipientsPerCampaign != null
                ? `Jusqu’à ${caps.maxEmailRecipientsPerCampaign} destinataires par email`
                : null
            }
          />
          <p className="mt-3 text-xs text-gray-500">
            Email marketing :{' '}
            <span className="font-medium text-gray-700">
              {marketingEnabled ? 'disponible' : 'temporairement indisponible'}
            </span>
          </p>
        </div>
        <div className="card">
          <QuotaMeter
            label="Campagnes In-App"
            used={inAppUsed}
            standardMax={inAppStandard}
            bonusMax={inAppBonus}
            totalMax={inAppTotal}
            remaining={inAppRemaining}
            bonusLines={inAppBonusLines}
            hint="Destinataires : sans limite"
          />
        </div>
      </div>

      {displayTier === 'INCLUS' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-100 bg-primary-50/50 px-4 py-3">
          <p className="text-sm text-gray-700">
            <span className="font-medium text-gray-900">Passez à Plus</span> pour communiquer plus
            souvent et toucher davantage de clients.
          </p>
          {typeof onDiscoverOffers === 'function' && (
            <button type="button" className="btn-primary text-sm whitespace-nowrap" onClick={onDiscoverOffers}>
              Découvrir les offres
            </button>
          )}
        </div>
      )}

      {displayTier === 'PLUS' && (
        <p className="text-sm text-gray-600">
          Vous êtes sur l’offre <span className="font-medium text-gray-900">Plus</span> — idéale pour
          un rythme de campagnes régulier.
        </p>
      )}
    </div>
  );
}
